import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Chip, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress,
    Paper, FormControl, Select, MenuItem, InputLabel,
} from '@mui/material';
import axiosInstance from '../../../../utils/axiosInstance';
import {
    OptionTypeLabels, OptionTypeColors,
    FlowSignalTypeLabels, FlowSignalTypeColors,
} from '../../stockTradingEnums';

const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';
const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
const formatDateTime = (val) => val ? new Date(val).toLocaleString() : '-';
const formatNumber = (val) => val != null ? Number(val).toLocaleString() : '-';
const formatGreek = (val) => val != null ? Number(val).toFixed(3) : '-';

const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, fontSize: '1rem' }}>{children}</Typography>
);
const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message || 'No data available'}</Typography></Box>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);

const EXPIRY_OPTIONS = [
    { label: 'Next 30 days', value: 30 },
    { label: 'Next 60 days', value: 60 },
    { label: 'Next 90 days', value: 90 },
    { label: 'Next 180 days', value: 180 },
    { label: 'All expirations', value: '' },
];

const STRIKE_OPTIONS = [
    { label: '\u00B14 strikes', value: 4 },
    { label: '\u00B18 strikes', value: 8 },
    { label: '\u00B116 strikes', value: 16 },
    { label: '\u00B124 strikes', value: 24 },
    { label: 'All strikes', value: '' },
];

const cellSx = { px: 0.75, py: 0.4, fontSize: '0.75rem', whiteSpace: 'nowrap' };
const headerCellSx = { ...cellSx, fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary' };

const OptionsTab = ({ data, loading, error, symbolId, symbolData }) => {
    const [expiryDays, setExpiryDays] = useState(90);
    const [strikeCount, setStrikeCount] = useState(8);
    const [localData, setLocalData] = useState(null);
    const [localLoading, setLocalLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const syncTriggeredRef = React.useRef(false);
    const retryRef = React.useRef(null);

    const currentPrice = symbolData?.current_price || data?.current_price || null;

    // Fetch options data from database via backend
    const fetchOptions = React.useCallback(async () => {
        if (!symbolId) return null;
        setLocalLoading(true);
        try {
            const params = new URLSearchParams();
            if (expiryDays) params.set('expiry_days', expiryDays);
            if (strikeCount) params.set('strike_count', strikeCount);
            if (currentPrice) params.set('current_price', currentPrice);
            const res = await axiosInstance.get(
                `/api/stock-trading/symbol-detail/${symbolId}/options?${params}`
            );
            if (res.data.success) {
                setLocalData(res.data.data);
                return res.data.data;
            }
        } catch (e) { /* use initial data */ }
        finally { setLocalLoading(false); }
        return null;
    }, [expiryDays, strikeCount, symbolId, currentPrice]);

    // Trigger sync: fetch from Yahoo Finance API -> store in DB -> then poll DB
    const triggerSync = React.useCallback(async (configId) => {
        if (!configId || syncTriggeredRef.current) return;
        syncTriggeredRef.current = true;
        setSyncing(true);
        try {
            await axiosInstance.post(`/api/stock-trading/global/options-chain/sync/${configId}`);
            // Sync done, fetch fresh data from DB
            await fetchOptions();
            setSyncing(false);
        } catch (e) {
            // Sync may take time, start polling
            setSyncing(true);
        }
    }, [fetchOptions]);

    // Initial fetch + trigger sync if no data
    useEffect(() => {
        const init = async () => {
            const result = await fetchOptions();
            if (result && (result.option_contracts || []).length === 0 && result.config_id) {
                triggerSync(result.config_id);
            }
        };
        syncTriggeredRef.current = false;
        init();
    }, [expiryDays, strikeCount, symbolId, currentPrice]); // eslint-disable-line react-hooks/exhaustive-deps

    // Poll every 10 seconds while syncing until data arrives
    useEffect(() => {
        if (syncing) {
            retryRef.current = setInterval(async () => {
                const result = await fetchOptions();
                if (result && (result.option_contracts || []).length > 0) {
                    setSyncing(false);
                }
            }, 10000);
        } else {
            if (retryRef.current) clearInterval(retryRef.current);
        }
        return () => { if (retryRef.current) clearInterval(retryRef.current); };
    }, [syncing, fetchOptions]);

    const activeData = localData || data;
    const contracts = activeData?.option_contracts || [];
    const oiSnapshots = activeData?.oi_snapshots || [];
    const optionsFlow = activeData?.options_flow || [];
    const impliedVolatility = activeData?.implied_volatility || [];

    // Group contracts by expiration, pair calls/puts by strike
    const chainData = useMemo(() => {
        if (!contracts.length) return [];
        const byExpiry = {};
        contracts.forEach(c => {
            const key = c.expiration_date;
            if (!byExpiry[key]) byExpiry[key] = {};
            const strikeKey = c.strike_price;
            if (!byExpiry[key][strikeKey]) byExpiry[key][strikeKey] = { strike: strikeKey, call: null, put: null };
            if (c.option_type === 'CALL') byExpiry[key][strikeKey].call = c;
            else byExpiry[key][strikeKey].put = c;
        });
        return Object.keys(byExpiry).sort().map(expiry => {
            const rows = Object.values(byExpiry[expiry]).sort((a, b) => a.strike - b.strike);
            const sample = rows[0]?.call || rows[0]?.put;
            const dte = sample?.dte ?? null;
            return { expiry, dte, rows };
        });
    }, [contracts]);

    if ((loading || localLoading) && !localData) return <TabLoading />;
    if (error && !localData) return <EmptyState message={error} />;
    if (!activeData) return <EmptyState />;

    const totalCols = 15;

    return (
        <Box>
            {/* Syncing Banner */}
            {syncing && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, px: 2, py: 1.5,
                    bgcolor: 'rgba(245, 158, 11, 0.1)', borderRadius: 1, border: '1px solid rgba(245, 158, 11, 0.3)',
                }}>
                    <CircularProgress size={16} sx={{ color: '#f59e0b' }} />
                    <Typography sx={{ fontSize: '0.85rem', color: '#92400e' }}>
                        Options chain data is being fetched in the background. This page will auto-refresh when data is ready.
                    </Typography>
                </Box>
            )}

            {/* Current Price Banner */}
            {currentPrice && symbolData && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, px: 2, py: 1,
                    bgcolor: 'rgba(239, 68, 68, 0.08)', borderRadius: 1, border: '1px solid rgba(239, 68, 68, 0.2)',
                }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {symbolData.symbol}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {formatPrice(currentPrice)}
                    </Typography>
                    {symbolData.previous_close && (() => {
                        const change = currentPrice - symbolData.previous_close;
                        const changePct = symbolData.previous_close ? (change / symbolData.previous_close * 100) : 0;
                        const isPositive = change >= 0;
                        return (
                            <Typography sx={{ fontSize: '0.8rem', color: isPositive ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                                {isPositive ? '+' : ''}{change.toFixed(2)} ({isPositive ? '+' : ''}{changePct.toFixed(2)}%)
                            </Typography>
                        );
                    })()}
                </Box>
            )}

            {/* Filter Controls */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mr: 1 }}>Options Chain</Typography>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Expiration</InputLabel>
                    <Select value={expiryDays} label="Expiration" onChange={(e) => setExpiryDays(e.target.value)}>
                        {EXPIRY_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Strikes</InputLabel>
                    <Select value={strikeCount} label="Strikes" onChange={(e) => setStrikeCount(e.target.value)}>
                        {STRIKE_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {localLoading && <CircularProgress size={20} />}
            </Box>

            {/* Options Chain Table */}
            {chainData.length === 0 ? <EmptyState message="No options chain data available" /> : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            {/* Row 1: CALLS / STRIKE / PUTS */}
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ ...headerCellSx, bgcolor: 'rgba(34, 197, 94, 0.08)', borderBottom: 'none', fontSize: '0.75rem', fontWeight: 700, color: '#22c55e' }}>
                                    CALLS
                                </TableCell>
                                <TableCell colSpan={3} align="center" sx={{ ...headerCellSx, bgcolor: 'rgba(0,0,0,0.03)', borderBottom: 'none', fontSize: '0.75rem', fontWeight: 700 }}>
                                </TableCell>
                                <TableCell colSpan={6} align="center" sx={{ ...headerCellSx, bgcolor: 'rgba(239, 68, 68, 0.08)', borderBottom: 'none', fontSize: '0.75rem', fontWeight: 700, color: '#ef4444' }}>
                                    PUTS
                                </TableCell>
                            </TableRow>
                            {/* Row 2: Column headers */}
                            <TableRow>
                                {/* Call columns */}
                                <TableCell align="right" sx={{ ...headerCellSx, bgcolor: 'rgba(34, 197, 94, 0.04)' }}>OI</TableCell>
                                <TableCell align="right" sx={{ ...headerCellSx, bgcolor: 'rgba(34, 197, 94, 0.04)' }}>Vol</TableCell>
                                <TableCell align="right" sx={{ ...headerCellSx, bgcolor: 'rgba(34, 197, 94, 0.04)' }}>Delta</TableCell>
                                <TableCell align="right" sx={{ ...headerCellSx, bgcolor: 'rgba(34, 197, 94, 0.04)' }}>Bid</TableCell>
                                <TableCell align="right" sx={{ ...headerCellSx, bgcolor: 'rgba(34, 197, 94, 0.04)' }}>Ask</TableCell>
                                <TableCell align="right" sx={{ ...headerCellSx, bgcolor: 'rgba(34, 197, 94, 0.04)' }}>Last</TableCell>
                                {/* Center columns */}
                                <TableCell align="center" sx={{ ...headerCellSx, bgcolor: 'rgba(0,0,0,0.03)' }}>IV</TableCell>
                                <TableCell align="center" sx={{ ...headerCellSx, bgcolor: 'rgba(0,0,0,0.05)', fontWeight: 700, fontSize: '0.75rem' }}>Strike</TableCell>
                                <TableCell align="center" sx={{ ...headerCellSx, bgcolor: 'rgba(0,0,0,0.03)' }}>Spread</TableCell>
                                {/* Put columns */}
                                <TableCell align="left" sx={{ ...headerCellSx, bgcolor: 'rgba(239, 68, 68, 0.04)' }}>Last</TableCell>
                                <TableCell align="left" sx={{ ...headerCellSx, bgcolor: 'rgba(239, 68, 68, 0.04)' }}>Bid</TableCell>
                                <TableCell align="left" sx={{ ...headerCellSx, bgcolor: 'rgba(239, 68, 68, 0.04)' }}>Ask</TableCell>
                                <TableCell align="left" sx={{ ...headerCellSx, bgcolor: 'rgba(239, 68, 68, 0.04)' }}>Delta</TableCell>
                                <TableCell align="left" sx={{ ...headerCellSx, bgcolor: 'rgba(239, 68, 68, 0.04)' }}>Vol</TableCell>
                                <TableCell align="left" sx={{ ...headerCellSx, bgcolor: 'rgba(239, 68, 68, 0.04)' }}>OI</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {chainData.map((group) => (
                                <React.Fragment key={group.expiry}>
                                    {/* Expiration Group Header */}
                                    <TableRow>
                                        <TableCell colSpan={totalCols} sx={{
                                            py: 0.75, px: 1.5, bgcolor: 'rgba(0,0,0,0.06)',
                                            fontWeight: 700, fontSize: '0.8rem', borderBottom: '2px solid rgba(0,0,0,0.1)',
                                        }}>
                                            {formatDate(group.expiry)}
                                            {group.dte != null && (
                                                <Typography component="span" sx={{ ml: 1.5, fontSize: '0.75rem', color: 'text.secondary', fontWeight: 400 }}>
                                                    {group.dte} DTE
                                                </Typography>
                                            )}
                                            {symbolData?.symbol && (
                                                <Typography component="span" sx={{ ml: 1.5, fontSize: '0.75rem', color: 'text.secondary', fontWeight: 400 }}>
                                                    {symbolData.symbol}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    {/* Strike Rows */}
                                    {group.rows.map((row) => {
                                        const callITM = currentPrice && row.strike < currentPrice;
                                        const putITM = currentPrice && row.strike > currentPrice;
                                        const callBg = callITM ? 'rgba(34, 197, 94, 0.06)' : 'transparent';
                                        const putBg = putITM ? 'rgba(239, 68, 68, 0.06)' : 'transparent';
                                        const isATM = currentPrice && Math.abs(row.strike - currentPrice) ===
                                            Math.min(...group.rows.map(r => Math.abs(r.strike - currentPrice)));
                                        const callSpread = row.call?.ask && row.call?.bid ? (row.call.ask - row.call.bid) : null;
                                        const putSpread = row.put?.ask && row.put?.bid ? (row.put.ask - row.put.bid) : null;
                                        const iv = row.call?.implied_volatility || row.put?.implied_volatility;

                                        return (
                                            <TableRow key={row.strike} sx={{
                                                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                                                ...(isATM && { borderTop: '2px solid #f59e0b', borderBottom: '2px solid #f59e0b' }),
                                            }}>
                                                {/* Call side */}
                                                <TableCell align="right" sx={{ ...cellSx, bgcolor: callBg }}>{formatNumber(row.call?.open_interest)}</TableCell>
                                                <TableCell align="right" sx={{ ...cellSx, bgcolor: callBg }}>{formatNumber(row.call?.volume)}</TableCell>
                                                <TableCell align="right" sx={{ ...cellSx, bgcolor: callBg }}>{formatGreek(row.call?.delta)}</TableCell>
                                                <TableCell align="right" sx={{ ...cellSx, bgcolor: callBg, color: '#22c55e', fontWeight: 500 }}>{formatPrice(row.call?.bid)}</TableCell>
                                                <TableCell align="right" sx={{ ...cellSx, bgcolor: callBg, color: '#ef4444', fontWeight: 500 }}>{formatPrice(row.call?.ask)}</TableCell>
                                                <TableCell align="right" sx={{ ...cellSx, bgcolor: callBg, fontWeight: 600 }}>{formatPrice(row.call?.last_price)}</TableCell>
                                                {/* Center */}
                                                <TableCell align="center" sx={{ ...cellSx, bgcolor: 'rgba(0,0,0,0.02)' }}>{formatPercent(iv)}</TableCell>
                                                <TableCell align="center" sx={{
                                                    ...cellSx, fontWeight: 700, fontSize: '0.8rem',
                                                    bgcolor: isATM ? 'rgba(245, 158, 11, 0.12)' : 'rgba(0,0,0,0.04)',
                                                    borderLeft: '2px solid rgba(0,0,0,0.08)',
                                                    borderRight: '2px solid rgba(0,0,0,0.08)',
                                                }}>
                                                    {formatPrice(row.strike)}
                                                </TableCell>
                                                <TableCell align="center" sx={{ ...cellSx, bgcolor: 'rgba(0,0,0,0.02)' }}>
                                                    {putSpread != null ? formatPrice(putSpread) : (callSpread != null ? formatPrice(callSpread) : '-')}
                                                </TableCell>
                                                {/* Put side */}
                                                <TableCell align="left" sx={{ ...cellSx, bgcolor: putBg, fontWeight: 600 }}>{formatPrice(row.put?.last_price)}</TableCell>
                                                <TableCell align="left" sx={{ ...cellSx, bgcolor: putBg, color: '#22c55e', fontWeight: 500 }}>{formatPrice(row.put?.bid)}</TableCell>
                                                <TableCell align="left" sx={{ ...cellSx, bgcolor: putBg, color: '#ef4444', fontWeight: 500 }}>{formatPrice(row.put?.ask)}</TableCell>
                                                <TableCell align="left" sx={{ ...cellSx, bgcolor: putBg }}>{formatGreek(row.put?.delta)}</TableCell>
                                                <TableCell align="left" sx={{ ...cellSx, bgcolor: putBg }}>{formatNumber(row.put?.volume)}</TableCell>
                                                <TableCell align="left" sx={{ ...cellSx, bgcolor: putBg }}>{formatNumber(row.put?.open_interest)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* OI Snapshots */}
            <SectionTitle>Open Interest Snapshots</SectionTitle>
            {oiSnapshots.length === 0 ? <EmptyState message="No OI snapshot data available" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="right">Strike Price</TableCell>
                                <TableCell>Expiration</TableCell>
                                <TableCell>Option Type</TableCell>
                                <TableCell align="right">Open Interest</TableCell>
                                <TableCell align="right">OI Change</TableCell>
                                <TableCell>Snapshot Time</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {oiSnapshots.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell align="right">{formatPrice(row.strike_price)}</TableCell>
                                    <TableCell>{formatDate(row.expiration_date)}</TableCell>
                                    <TableCell>{row.option_type || '-'}</TableCell>
                                    <TableCell align="right">{formatNumber(row.open_interest)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.oi_change)}</TableCell>
                                    <TableCell>{formatDateTime(row.snapshot_timestamp)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Options Flow */}
            <SectionTitle>Options Flow</SectionTitle>
            {optionsFlow.length === 0 ? <EmptyState message="No options flow data available" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Flow Signal</TableCell>
                                <TableCell>Option Type</TableCell>
                                <TableCell align="right">Strike</TableCell>
                                <TableCell align="right">Volume</TableCell>
                                <TableCell align="right">Premium Total</TableCell>
                                <TableCell>Implied Direction</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {optionsFlow.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        {row.flow_signal_type ? (
                                            <Chip
                                                label={FlowSignalTypeLabels[row.flow_signal_type] || row.flow_signal_type}
                                                color={FlowSignalTypeColors[row.flow_signal_type] || 'default'}
                                                size="small"
                                            />
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {row.option_type ? (
                                            <Chip
                                                label={OptionTypeLabels[row.option_type] || row.option_type}
                                                color={OptionTypeColors[row.option_type] || 'default'}
                                                size="small"
                                            />
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(row.strike_price)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.volume)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.premium_total)}</TableCell>
                                    <TableCell>{row.implied_direction || '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Implied Volatility */}
            <SectionTitle>Implied Volatility</SectionTitle>
            {impliedVolatility.length === 0 ? <EmptyState message="No implied volatility data available" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="right">IV Value</TableCell>
                                <TableCell align="right">IV Percentile</TableCell>
                                <TableCell align="right">RV Value</TableCell>
                                <TableCell align="right">IV-RV Difference</TableCell>
                                <TableCell>Fetch Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {impliedVolatility.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell align="right">{formatPercent(row.iv_value)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.iv_percentile)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.rv_value)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.iv_rv_difference)}</TableCell>
                                    <TableCell>{formatDate(row.fetch_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default OptionsTab;
